/**
 * Curated `beforeSend` filter, shared by every Sentry client — web AND native.
 *
 * Pure and SDK-agnostic on purpose: no imports, and it inspects only
 * `exception.values[0].type/value` and `message`, so the same list serves
 * `initErrorTracking` (browser) and `initErrorTrackingRN` (React Native). The
 * two media entries cannot fire on native and cost nothing there; the two Clerk
 * entries fire on BOTH, which is why leaving mobile unwired left the same
 * expected-condition noise on the one tier that cannot be rolled back.
 *
 * `initErrorTracking` has always accepted a `beforeSend` and forwarded it to
 * `Sentry.init` — and NO app ever passed one. Nothing was classified before it
 * became an alert, so on 2026-09-10 five of the six unresolved production issues
 * were expected conditions: two media-permission declines and three Clerk
 * outcomes (a session expiring, and a signup form telling a user their email is
 * already taken). An error channel that is ~83% expected behaviour is one nobody
 * reads, and alert rules built on it either fire constantly or get muted — which
 * is how a real regression goes unnoticed.
 *
 * ─── Rules for editing this list ──────────────────────────────────────────────
 *
 * 1. CURATED AND EXPLICIT. Never a broad pattern match. An over-eager filter is
 *    how a real error disappears, and that failure is silent and permanent.
 * 2. Every entry states WHY the condition is expected. If you cannot write that
 *    sentence, it does not belong here.
 * 3. Prefer DOWNGRADE over DROP wherever the RATE carries information. Stopping
 *    the alert is the goal; losing the signal is not. A spike in camera denials
 *    after a release means an iframe lost `allow="camera; microphone"` — you
 *    want to still be able to see that.
 * 4. Match on structured fields (`exception.type`, error name) before message
 *    text. Message strings are provider copy and change without warning.
 */

/** Minimal shape of the Sentry event fields this filter inspects. */
export interface BeforeSendEventLike {
  level?: string;
  exception?: { values?: Array<{ type?: string; value?: string }> };
  message?: string;
}

interface ExpectedCondition {
  /** Short id used in tests and when reasoning about coverage. */
  id: string;
  /** Why this is an expected outcome rather than a defect. */
  why: string;
  /** `drop` removes the event; `warning` keeps it, off the error alert rules. */
  action: 'drop' | 'warning';
  matches: (type: string, value: string) => boolean;
}

const EXPECTED: ExpectedCondition[] = [
  {
    id: 'media-permission-declined',
    why: 'The user declined or dismissed a camera/microphone prompt. A choice, not a fault. Kept as a warning because a SPIKE means a Permissions-Policy or iframe `allow` regression.',
    action: 'warning',
    matches: (type, value) =>
      type === 'NotAllowedError' ||
      type === 'PermissionDeniedError' ||
      /\bNotAllowedError\b/.test(value),
  },
  {
    id: 'media-device-missing',
    why: 'No camera/microphone attached, or none matching the constraints. Environmental, not a defect.',
    action: 'warning',
    matches: (type, value) => type === 'NotFoundError' || /\bDevicesNotFoundError\b/.test(value),
  },
  {
    id: 'clerk-session-expired',
    why: 'Clerk reporting an expired session ("You are signed out"). Normal lifecycle — the user is redirected to sign in.',
    action: 'warning',
    matches: (_type, value) => /you are signed out/i.test(value),
  },
  {
    id: 'clerk-validation-email-taken',
    why: 'A Clerk VALIDATION MESSAGE shown to the user during signup, not a thrown defect. NOTE: filtering it must not hide that the underlying identity-collision problem is real — that is tracked separately as the invitation-funnel work, which is where it belongs.',
    action: 'warning',
    matches: (_type, value) => /that email address is taken/i.test(value),
  },
  {
    id: 'clerk-reverification-cancelled',
    why: "The user dismissed Clerk's step-up (re-verification) modal. A choice, not a fault: `useStepUp` already turns it into a silent `cancelled` outcome, and this is the defence in depth for any call site that still lets the rejection escape (F40, JOURNEY-65). Dropped rather than downgraded: a cancel rate is not a product signal. Matched on Clerk's runtime error TYPE plus its structured `code=` suffix, never on the prose, so a real ClerkRuntimeError still reports.",
    action: 'drop',
    matches: (type, value) =>
      type === 'ClerkRuntimeError' && value.includes('(code="reverification_cancelled")'),
  },
  {
    id: 'network-abort-on-unload',
    why: 'A fetch aborted because the user navigated away mid-request. Dropped rather than downgraded: the rate carries no product signal and it is pure noise.',
    action: 'drop',
    matches: (type, value) =>
      type === 'AbortError' ||
      /the operation was aborted/i.test(value) ||
      /network ?error.*(unload|navigat)/i.test(value),
  },
];

/**
 * Classify an event against the curated list. Exported for testing so each
 * entry can be asserted individually — and so an ADJACENT real error can be
 * asserted to still report.
 */
export function classifyExpectedCondition(
  event: BeforeSendEventLike
): ExpectedCondition | undefined {
  const first = event.exception?.values?.[0];
  const type = first?.type ?? '';
  const value = first?.value ?? event.message ?? '';
  if (!type && !value) return undefined;
  return EXPECTED.find((c) => c.matches(type, value));
}

/**
 * The filter itself. Returns `null` to drop, or the event (possibly with a
 * lowered level) to keep.
 */
export function createBeforeSend() {
  // Signature matches `ErrorTrackingInitConfig['beforeSend']`, which is
  // deliberately SDK-agnostic (`Record<string, unknown>`) so this package does
  // not take a hard type dependency on @sentry/react.
  return (event: Record<string, unknown>): Record<string, unknown> | null => {
    const match = classifyExpectedCondition(event as BeforeSendEventLike);
    if (!match) return event;
    if (match.action === 'drop') return null;
    return { ...event, level: 'warning' };
  };
}
